import api from './api';
import { waitForJob } from './templateAssistantService';

// BILLING DETAILS — the Billing tab on the hotel, chain settings, partner settings and the vendor page.
// One endpoint for all four (ui-v2-billing); `entity` is 'hotel' | 'chain' | 'partner' | 'vendor'.
// The lambda decides who may edit and returns `canEdit` / `canEditRestricted` with the details.
const billingService = {
  get: async (entity, id) => {
    const response = await api.get(`/billing/${entity}/${id}`);
    return response.data;
  },

  // body: { details: { BILLINGCONTACT, … }, billTo?, billToCredentialId?, periods?, priceLines? } — billTo / periods /
  // priceLines are hotel-only. periods: [{ from: 'YYYY-MM-DD', billTo, billToCredentialId, to? (last only, V662),
  // accountContactName … frequency (V663) }] replaces the hotel's dated schedule; priceLines the hotel's prices.
  save: async (entity, id, body) => {
    const response = await api.put(`/billing/${entity}/${id}`, body);
    return response.data;
  },

  // A PARTNER'S BILLING TEMPLATES (V663) — Partner Settings > Templates > Billing. Partner admins and superadmins.
  listTemplates: async (partnerId) => (await api.get(`/billing/partner/${partnerId}/templates`)).data,
  getTemplate: async (partnerId, templateId) => (await api.get(`/billing/partner/${partnerId}/templates/${templateId}`)).data,
  createTemplate: async (partnerId, body) => (await api.post(`/billing/partner/${partnerId}/templates`, body)).data,
  updateTemplate: async (partnerId, templateId, body) => (await api.put(`/billing/partner/${partnerId}/templates/${templateId}`, body)).data,
  deleteTemplate: async (partnerId, templateId) => (await api.delete(`/billing/partner/${partnerId}/templates/${templateId}`)).data,
  // THE TEMPLATE ASSISTANT — a job on the server (a whole template outlasts API Gateway's 29 s); waits for the reply.
  // template: { name, body } as the editor holds it now. -> { data: { reply } }
  templateAssistant: async (partnerId, messages, template) => {
    const started = (await api.post(`/billing/partner/${partnerId}/templates/assistant`, { messages, template })).data;
    return waitForJob(started && started.jobId,
      async (jobId) => (await api.get(`/billing/partner/${partnerId}/templates/assistant/${jobId}`)).data);
  },

  // A PARTNER'S SIGNATURES (V667) — Partner Settings > Templates > Billing > Signatures. Partner admins and superadmins.
  listSignatures: async (partnerId) => (await api.get(`/billing/partner/${partnerId}/signatures`)).data,
  createSignature: async (partnerId, body) => (await api.post(`/billing/partner/${partnerId}/signatures`, body)).data,
  deleteSignature: async (partnerId, signatureId) =>
    (await api.delete(`/billing/partner/${partnerId}/signatures/${signatureId}`)).data,

  // A HOTEL'S BILLING DOCUMENTS (V663, ui-v2-billing-documents) — PDFs generated for one billing period.
  listDocuments: async (hotelId, periodFrom) =>
    (await api.get(`/billing/hotel/${hotelId}/documents`, { params: periodFrom ? { periodFrom } : {} })).data,
  generateDocument: async (hotelId, periodFrom, templateId) =>
    (await api.post(`/billing/hotel/${hotelId}/documents`, { periodFrom, templateId })).data,
  // { document, pdfBase64 } — the PDF itself, not a link (the lambda cannot wear the presigner's layer beside chromium).
  documentLink: async (hotelId, documentId) => (await api.get(`/billing/hotel/${hotelId}/documents/${documentId}`)).data,
  deleteDocument: async (hotelId, documentId) => (await api.delete(`/billing/hotel/${hotelId}/documents/${documentId}`)).data,
  // Signing (V666): { action: 'upload-url' } → { url, key, maxBytes }; { action: 'signed', key } and
  // { action: 'countersign' } → { document }.
  documentAction: async (hotelId, documentId, body) =>
    (await api.post(`/billing/hotel/${hotelId}/documents/${documentId}`, body)).data,
};

export default billingService;
